// The body of a guide: Markdown (src/lib/miniMarkdown: headings, paragraphs, lists, block quotes, tables, inline
// **bold**, *italic*, `code`, [links](/path)) plus five blocks of our own, each a ":::" container:
//
//   :::callout tip|note|warning|legal Optional title
//   Markdown (paragraphs and lists).
//   :::
//
//   :::figure <image id from ./images.ts>
//   alt: What the image shows, for screen readers.
//   caption: The visible caption.
//   credit: Optional credit line.
//   frame: browser | phone | none   (optional; a browser or phone frame around a screenshot)
//   :::
//
//   :::clip <clip id from ./clips.ts>
//   alt: What the clip shows, step by step, for screen readers (and the poster's alt).
//   caption: The visible caption.
//   :::
//
//   :::diagram <diagram id from src/components/guides/GuideDiagrams.tsx>
//   caption: The visible caption.
//   :::
//
//   :::link /feature-page
//   title: Link title
//   body: One line on what's there.
//   :::
//
//   :::video https://www.youtube.com/watch?v=…   (YouTube, Vimeo, Loom or Wistia only: videoEmbedUrl)
//   title: What the video shows (the iframe's title, for screen readers).
//   :::
//
// HTML comments (<!-- TODO(owner): … -->) are notes for the owner: they never render, and guideTodos() lists them.
// "## Heading {#id}" sets a heading's anchor; otherwise it's made from the text.

import { parseMarkdown, type MdBlock } from "@/lib/miniMarkdown";

export type CalloutTone = "tip" | "note" | "warning" | "legal";
export type FigureFrame = "browser" | "phone" | "none";

export type GuideBlock =
  | Exclude<MdBlock, { type: "heading" }>
  | { type: "heading"; level: 1 | 2 | 3; text: string; id: string }
  | { type: "callout"; tone: CalloutTone; title?: string; blocks: GuideBlock[] }
  | { type: "figure"; image: string; alt: string; caption?: string; credit?: string; frame: FigureFrame }
  | { type: "clip"; clip: string; alt: string; caption?: string }
  | { type: "diagram"; id: string; caption?: string }
  | { type: "link"; to: string; title: string; body?: string }
  | { type: "video"; src: string; title: string };

/**
 * The privacy-friendly embed URL for a video link from a known host (YouTube via youtube-nocookie.com, Vimeo with
 * do-not-track, Loom, Wistia), or null for anything else: a body can't embed an arbitrary page.
 */
export const videoEmbedUrl = (raw: string): string | null => {
  let u: URL;
  try {
    u = new URL(raw.trim().replace(/^\/\//, "https://"));
  } catch {
    return null;
  }
  if (u.protocol !== "https:" && u.protocol !== "http:") return null;
  const host = u.hostname.toLowerCase().replace(/^www\./, "");
  const id = (re: RegExp, s: string) => re.exec(s)?.[1] ?? null;
  if (host === "youtube.com" || host === "youtube-nocookie.com" || host === "m.youtube.com" || host === "youtu.be") {
    const v =
      host === "youtu.be"
        ? id(/^\/([\w-]{6,})/, u.pathname)
        : (u.searchParams.get("v") ?? id(/^\/(?:embed|shorts|live)\/([\w-]{6,})/, u.pathname));
    return v && /^[\w-]+$/.test(v) ? `https://www.youtube-nocookie.com/embed/${v}` : null;
  }
  if (host === "vimeo.com" || host === "player.vimeo.com") {
    const v = id(/(?:^|\/)(\d{5,})(?:$|[/?])/, u.pathname);
    return v ? `https://player.vimeo.com/video/${v}?dnt=1` : null;
  }
  if (host === "loom.com") {
    const v = id(/^\/(?:share|embed)\/([0-9a-f]{16,})/, u.pathname);
    return v ? `https://www.loom.com/embed/${v}` : null;
  }
  if (host === "fast.wistia.net" || host === "fast.wistia.com" || host.endsWith(".wistia.com")) {
    const v = id(/\/(?:embed\/iframe|medias)\/([a-z0-9]{6,})/i, u.pathname);
    return v ? `https://fast.wistia.net/embed/iframe/${v}` : null;
  }
  return null;
};

const TONES: CalloutTone[] = ["tip", "note", "warning", "legal"];

/** "Bib search: when it works" → "bib-search-when-it-works". */
export const slugify = (text: string): string =>
  text
    .toLowerCase()
    .replace(/\*\*|`|\*/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/** The owner's notes: every <!-- … --> comment's text. */
export const htmlComments = (src: string): string[] => [...src.matchAll(/<!--([\s\S]*?)-->/g)].map((m) => m[1].trim());

export const stripComments = (src: string): string => src.replace(/<!--[\s\S]*?-->/g, "");

const fields = (lines: string[], where: string): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const l of lines) {
    if (!l.trim()) continue;
    const m = /^(\w+):\s+(.*)$/.exec(l.trim());
    if (!m) throw new Error(`${where}: can't read "${l.trim()}"`);
    out[m[1]] = m[2].trim();
  }
  return out;
};

const withIds = (blocks: MdBlock[], used: Set<string>): GuideBlock[] =>
  blocks.map((b) => {
    if (b.type !== "heading") return b;
    const custom = /\s*\{#([\w-]+)\}\s*$/.exec(b.text);
    const text = custom ? b.text.slice(0, custom.index).trim() : b.text;
    let id = custom ? custom[1] : slugify(text) || "section";
    if (!custom) {
      const base = id;
      for (let n = 2; used.has(id); n += 1) id = `${base}-${n}`;
    }
    used.add(id);
    return { type: "heading", level: b.level, text, id };
  });

/** Parses a guide body (front matter already removed) into blocks. Throws on a malformed ":::" block. */
export function parseGuideBody(src: string, used: Set<string> = new Set()): GuideBlock[] {
  const lines = stripComments(src).replace(/\r\n?/g, "\n").split("\n");
  const out: GuideBlock[] = [];
  let md: string[] = [];
  const flush = () => {
    if (md.length) out.push(...withIds(parseMarkdown(md.join("\n")), used));
    md = [];
  };
  for (let i = 0; i < lines.length; i += 1) {
    const open = /^:::(\w+)(?:\s+(.*))?$/.exec(lines[i].trim());
    if (!open) {
      md.push(lines[i]);
      continue;
    }
    flush();
    const [, kind, arg = ""] = open;
    const inner: string[] = [];
    let j = i + 1;
    while (j < lines.length && lines[j].trim() !== ":::") inner.push(lines[j++]);
    if (j >= lines.length) throw new Error(`:::${kind} on line ${i + 1} is never closed`);
    i = j;
    const where = `:::${kind} on line ${i + 1}`;
    if (kind === "callout") {
      const [tone, ...title] = arg.trim().split(/\s+/);
      if (!TONES.includes(tone as CalloutTone)) throw new Error(`${where}: tone must be one of ${TONES.join(", ")}`);
      out.push({ type: "callout", tone: tone as CalloutTone, title: title.join(" ") || undefined, blocks: parseGuideBody(inner.join("\n"), used) });
    } else if (kind === "figure") {
      const f = fields(inner, where);
      if (!arg.trim() || !f.alt) throw new Error(`${where}: needs an image id and alt:`);
      const frame = (f.frame ?? "none") as FigureFrame;
      if (!["browser", "phone", "none"].includes(frame)) throw new Error(`${where}: frame must be browser, phone or none`);
      out.push({ type: "figure", image: arg.trim(), alt: f.alt, caption: f.caption, credit: f.credit, frame });
    } else if (kind === "clip") {
      const f = fields(inner, where);
      if (!arg.trim() || !f.alt) throw new Error(`${where}: needs a clip id and alt:`);
      out.push({ type: "clip", clip: arg.trim(), alt: f.alt, caption: f.caption });
    } else if (kind === "diagram") {
      const f = fields(inner, where);
      if (!arg.trim()) throw new Error(`${where}: needs a diagram id`);
      out.push({ type: "diagram", id: arg.trim(), caption: f.caption });
    } else if (kind === "link") {
      const f = fields(inner, where);
      if (!arg.trim().startsWith("/") || !f.title) throw new Error(`${where}: needs a /path and title:`);
      out.push({ type: "link", to: arg.trim(), title: f.title, body: f.body });
    } else if (kind === "video") {
      const f = fields(inner, where);
      const src = videoEmbedUrl(arg);
      if (!src || !f.title) throw new Error(`${where}: needs a YouTube, Vimeo, Loom or Wistia URL and title:`);
      out.push({ type: "video", src, title: f.title });
    } else {
      throw new Error(`${where}: unknown block`);
    }
  }
  flush();
  return out;
}

/** The plain text of inline Markdown (for word counts and JSON-LD). */
export const plainInline = (s: string): string =>
  s
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/\*\*|`/g, "")
    .replace(/(^|[^*])\*([^*\s][^*]*)\*/g, "$1$2");

/** All the visible text of some blocks, in reading order. */
export function blocksText(blocks: GuideBlock[]): string[] {
  return blocks.flatMap((b): string[] => {
    switch (b.type) {
      case "heading":
      case "paragraph":
      case "quote":
        return [plainInline(b.text)];
      case "list":
        return b.items.map(plainInline);
      case "table":
        return [...b.head, ...b.rows.flat()].map(plainInline);
      case "callout":
        return [...(b.title ? [b.title] : []), ...blocksText(b.blocks)];
      case "figure":
        return [b.caption ?? "", b.credit ?? ""].filter(Boolean).map(plainInline);
      case "diagram":
      case "clip":
        return b.caption ? [plainInline(b.caption)] : [];
      case "link":
        return [b.title, b.body ?? ""].filter(Boolean);
      case "video":
        return [b.title];
      default:
        return [];
    }
  });
}

export const countWords = (texts: string[]): number =>
  texts.join(" ").split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w)).length;

/** Every internal link (/path) in some blocks: inline Markdown links and :::link blocks. */
export function internalLinks(blocks: GuideBlock[]): string[] {
  const fromText = (s: string) => [...s.matchAll(/\]\((\/[^)\s]*)\)/g)].map((m) => m[1]);
  return blocks.flatMap((b): string[] => {
    switch (b.type) {
      case "paragraph":
      case "quote":
      case "heading":
        return fromText(b.text);
      case "list":
        return b.items.flatMap(fromText);
      case "table":
        return [...b.head, ...b.rows.flat()].flatMap(fromText);
      case "callout":
        return internalLinks(b.blocks);
      case "link":
        return [b.to];
      default:
        return [];
    }
  });
}
